import React, {
  useCallback,
  useEffect,
  useMemo,
  useState,
  useRef,
} from "react";
import axios from "axios";
import { getOrgList } from "../../../../api";
import Plus from "../../../assets/svg/icons/common/Plus";
import ButtonIcon from "../../../components/ButtonIcon/ButtonIcon";
import SearchInput from "../../../components/SearchInput/SearchInput";
import Table from "../../../components/Table";
import useCommonStore from "../../../stores/common";
import useOrganizationStore from "../../../stores/organization";
import useUserStore from "../../../stores/user";
import Pagination from "../../../components/Pagination/Pagination";
import TrendDown from "../../../assets/svg/icons/common/TrendDown";
import TrendUp from "../../../assets/svg/icons/common/TrendUp";
import { useMediaQuery } from "../../../hooks/useMediaQuery";
import SkeletonLoader from "../../../components/skeleton-loader";
import pageHeadingStyles from "../../../styles/PageHeader.module.scss";
import styles from "./styles.module.scss";

const getOrganizationImageSrc = (slug) => {
  const count = localStorage.getItem("counter")
    ? localStorage.getItem("counter")
    : "";
  return `/img/${slug}/logo-solid.png?${count}`;
};

const TruncatedCell = ({ value }) => {
  const ref = useRef(null);
  const [isTruncated, setIsTruncated] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const check = () => setIsTruncated(el.scrollWidth > el.clientWidth);
    check();
    const observer = new ResizeObserver(check);
    observer.observe(el);
    return () => observer.disconnect();
  }, [value]);

  return (
    <span
      ref={ref}
      title={isTruncated ? value : undefined}
      style={{
        display: "block",
        overflow: "hidden",
        textOverflow: "ellipsis",
        whiteSpace: "nowrap",
      }}
    >
      {value}
    </span>
  );
};

const List = () => {
  const user = useUserStore((state) => state.user);
  const userLoading = useUserStore((state) => state.userLoading);
  const [organizationDataLoading, setOrganizationDataLoading] = useState(false);
  const organizationList = useOrganizationStore(
    (state) => state.organizationList,
  );
  const setOrganizationList = useOrganizationStore(
    (state) => state.setOrganizationList,
  );
  const setOrgFilterSearchQuery = useOrganizationStore(
    (state) => state.setOrgFilterSearchQuery,
  );
  const orgFilterSearchQuery = useOrganizationStore(
    (state) => state.orgFilterSearchQuery,
  );
  const selectedOrganizationId = useOrganizationStore(
    (state) => state.selectedOrganizationId,
  );
  const setSelectedOrganizationId = useOrganizationStore(
    (state) => state.setSelectedOrganizationId,
  );
  const setPageCount = useOrganizationStore((state) => state.setPageCount);
  const pageCount = useOrganizationStore((state) => state.pageCount);
  const showDetails = useCommonStore((state) => state.showDetails);
  const setShowDetails = useCommonStore((state) => state.setShowDetails);
  const setAddOrganizationModalOpen = useOrganizationStore(
    (state) => state.setAddOrganizationModalOpen,
  );
  const totalItems = useOrganizationStore((state) => state.totalItems);
  const itemsPerPage = useOrganizationStore((state) => state.itemsPerPage);
  const setTotalItems = useOrganizationStore((state) => state.setTotalItems);
  const setItemsPerPage = useOrganizationStore(
    (state) => state.setItemsPerPage,
  );
  const [searchText, setSearchText] = useState("");
  const [sortBy, setSortBy] = useState("");
  const [sortColumn, setSortColumn] = useState("ASC");
  const cancelRef = useRef(null);
  const ignoreRef = useRef(false);
  const debounceTimeoutRef = useRef(null);
  const isMobile = useMediaQuery(768);

  const getOrganizationType = (type) => {
    const types = {
      hands_on: "Agent/ISO",
      referral_partner: "Referral Partner",
      hands_off: "Referral Partner",
      isv: "ISV",
    };
    return types[type] || type;
  };

  const onRowClick = useCallback(
    (row) => {
      setSelectedOrganizationId(row.id);
      setShowDetails(true);
    },
    [setSelectedOrganizationId, setShowDetails],
  );

  const columns = useMemo(
    () => [
      {
        headerName: "ID",
        field: "id",
        width: "100px",
        allowSort: false,
        hideCell: showDetails,
      },
      {
        headerName: "ORGANISATION NAME",
        field: "name",
        width: "236px",
        allowSort: false,
        renderCell: (value, row) => (
          <div className={styles.orgNameCell}>
            <div className={styles.orgNameCellImage}>
              <img src={getOrganizationImageSrc(row.logo_slug || row.whitelabel_slug)} alt="" />
            </div>
            <span className={styles.orgName}>{value}</span>
          </div>
        ),
      },
      {
        headerName: "OFFICES",
        field: "ems_offices",
        allowSort: false,
        renderCell: (value) =>
          value ? <TruncatedCell value={value} /> : "-",
        hideCell: showDetails,
      },
      {
        headerName: "MTD PROCESSING VOLUME",
        field: "monthly_volume",
        allowSort: false,
        renderCell: (value, row) => {
          return row?.mtd_processing_volume
            ? `$${Number(row?.mtd_processing_volume).toLocaleString()}`
            : "$0";
        },
      },
      {
        headerName: "OPEN APPS",
        field: "submitting_apps",
        allowSort: false,
        renderCell: (value, row) => {
          return row?.open_applications?.count
            ? `${row?.open_applications?.percentage}% (${row?.open_applications?.count})`
            : "0% (0)";
        },
      },
      {
        headerName: "CB Ratio",
        field: "cb_ratio",
        allowSort: false,
        renderCell: (value, row) => {
          return row?.chargeback.amount
            ? `${row?.chargeback.percentage}% (${row?.chargeback.amount.toLocaleString()})`
            : "0% (0)";
        },
        hideCell: showDetails,
      },
      {
        headerName: "Non-Activated Accounts",
        field: "approved_apps",
        allowSort: false,
        renderCell: (value, row) => {
          return row?.non_active_accounts?.count
            ? `${row?.non_active_accounts?.count}`
            : "0";
        },
      },
      {
        headerName: "% NON-ACTIVE",
        field: "non_active_percentage",
        allowSort: false,
        renderCell: (value, row) => {
          return row?.non_active_accounts?.percentage
            ? `${row?.non_active_accounts?.percentage}%`
            : "0%";
        },
        hideCell: showDetails,
      },
      {
        headerName: "APPROVALS (MON)",
        field: "approval_last_month",
        allowSort: false,
        renderCell: (value, row) => {
          return row?.approved.count
            ? `${row?.approved.percentage}% (${row?.approved.count})`
            : "0% (0)";
        },
        hideCell: showDetails,
      },
      {
        headerName: "TYPE",
        field: "type",
        width: "151px",
        allowSort: false,
        renderCell: (value) => getOrganizationType(value),
        hideCell: showDetails,
      },
    ],
    [showDetails],
  );

  const fetchData = useCallback(() => {
    if (!user) {
      return;
    }

    if (cancelRef.current) {
      cancelRef.current.cancel();
    }

    cancelRef.current = axios.CancelToken.source();
    ignoreRef.current = false;

    setOrganizationDataLoading(true);

    getOrgList(
      {
        page: orgFilterSearchQuery.page,
        searchTerm:
          orgFilterSearchQuery?.search.length > 0
            ? orgFilterSearchQuery.search
            : "",
        sort_by: sortColumn,
        sort: sortBy,
        include_trends: isMobile,
      },
      cancelRef.current.token,
    )
      .then((orgListResponse) => {
        if (ignoreRef.current) {
          return;
        }

        if (orgListResponse.status) {
          setOrganizationDataLoading(false);
          setOrganizationList(orgListResponse.data.organizations);
          setTotalItems(orgListResponse.data.pagination.total);
          setItemsPerPage(orgListResponse.data.pagination.per_page);
          setPageCount(orgListResponse.data.pagination.last_page);
        }
      })
      .catch((error) => {
        if (axios.isCancel(error)) {
          return;
        }
        setOrganizationDataLoading(false);
      });
  }, [
    orgFilterSearchQuery.page,
    orgFilterSearchQuery.search,
    setOrganizationList,
    setPageCount,
    setTotalItems,
    setItemsPerPage,
    user,
    sortBy,
    sortColumn,
    isMobile,
  ]);

  useEffect(() => {
    if (!user) {
      return;
    }

    fetchData();
  }, [user, orgFilterSearchQuery.page, orgFilterSearchQuery.search, fetchData]);

  useEffect(() => {
    return () => {
      ignoreRef.current = true;
      if (cancelRef.current) {
        cancelRef.current.cancel();
      }
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
      }
    };
  }, []);

  const handleSort = (field) => {
    setSortBy(field);
    setSortColumn(sortColumn === "ASC" ? "DESC" : "ASC");
  };

  const onChangeSearchInput = (e) => {
    const value = e.target.value;
    setSearchText(value);

    if (debounceTimeoutRef.current) {
      clearTimeout(debounceTimeoutRef.current);
    }

    debounceTimeoutRef.current = setTimeout(() => {
      setOrgFilterSearchQuery({
        search: value,
        page: 1,
      });
    }, 500);
  };

  return (
    <>
      <div className={styles.mobile_header}>
        <div className={styles.mobile_search_wrapper}>
          <SearchInput
            onChange={onChangeSearchInput}
            searchValue={searchText}
            className={styles.mobile_search_input}
          />
        </div>
        <button
          className={styles.mobile_add_button}
          onClick={() => setAddOrganizationModalOpen(true)}
        >
          <Plus />
        </button>
      </div>

      <div
        className={`${pageHeadingStyles.page_heading} ${pageHeadingStyles.column}`}
      >
        <div className={styles.title_row}>
          <div className={pageHeadingStyles.title_contents}>
            <p>Organizations</p>
          </div>
          <div className={styles.right_side}>
            <ButtonIcon
              bgColor="#4C70EF"
              textColor="#fff"
              onClick={() => setAddOrganizationModalOpen(true)}
            >
              <Plus />
              <span>Add Organization</span>
            </ButtonIcon>
          </div>
        </div>
        <div className={styles.search_row}>
          <SearchInput
            onChange={onChangeSearchInput}
            searchValue={searchText}
          />
        </div>
      </div>

      <div className={styles.card}>
        <div className={styles.mobile_table_wrapper}>
          <Table
            layout="fixed"
            noMarginTop
            columns={columns}
            data={organizationList}
            sortBy={sortBy}
            sortColumn={sortColumn}
            onSortClick={handleSort}
            onRowClick={onRowClick}
            selectedRowId={selectedOrganizationId}
            enableSkeleton={true}
            dataLoading={organizationDataLoading || userLoading}
          />
        </div>

        <div className={styles.mobile_cards_container}>
          {organizationDataLoading || userLoading ? (
            Array.from({ length: 5 }).map((_, index) => (
              <div key={`skeleton-${index}`} className={styles.mobile_card_skeleton}>
                <div className={styles.mobile_card_left}>
                  <SkeletonLoader
                    variant="rectangular"
                    width="32px"
                    height="32px"
                    style={{ borderRadius: "4px" }}
                  />
                  <div className={styles.mobile_card_info}>
                    <SkeletonLoader variant="text" width="120px" height="16px" />
                    <SkeletonLoader variant="text" width="80px" height="14px" />
                  </div>
                </div>
                <div className={styles.mobile_card_right}>
                  <SkeletonLoader variant="text" width="70px" height="16px" />
                  <SkeletonLoader
                    variant="rectangular"
                    width="16px"
                    height="16px"
                    style={{ borderRadius: "4px" }}
                  />
                </div>
              </div>
            ))
          ) : !organizationList?.length ? (
            <div className={styles.noData}>No data to display</div>
          ) : (
            organizationList.map((row) => {
              const volume = row?.mtd_processing_volume
                ? `$${Number(row?.mtd_processing_volume).toLocaleString()}`
                : "$0";
              const orgType = getOrganizationType(row.type);
              const isSelected = selectedOrganizationId === row.id;
              
              const getTrendIcon = () => {
                const volumeValue = row?.mtd_processing_volume || 0;
                
                if (volumeValue === 0 || !row?.mtd_volume_trend) {
                  return null;
                }
                
                return row.mtd_volume_trend === 'up' ? <TrendUp /> : <TrendDown />;
              };

              return (
                <div
                  key={row.id}
                  className={`${styles.mobile_card} ${isSelected ? styles.selected : ""}`}
                  onClick={() => onRowClick(row)}
                >
                  <div className={styles.mobile_card_left}>
                    <div className={styles.mobile_card_avatar}>
                      <img
                        src={getOrganizationImageSrc(row.logo_slug || row.whitelabel_slug)}
                        alt=""
                      />
                    </div>
                    <div className={styles.mobile_card_info}>
                      <div className={styles.mobile_card_name}>{row.name}</div>
                      <div className={styles.mobile_card_type}>{orgType}</div>
                    </div>
                  </div>
                  <div className={styles.mobile_card_right}>
                    <div className={styles.mobile_card_volume}>{volume}</div>
                    <div className={styles.mobile_card_trend}>
                      {getTrendIcon()}
                    </div>
                  </div>
                </div>
              );
            })
          )}
        </div>

        {organizationList?.length > 0 && (
          <div className={styles.pagination_container}>
            <Pagination
              pageCount={pageCount}
              currentPage={orgFilterSearchQuery.page - 1}
              onPageChange={({ selected }) => {
                setOrgFilterSearchQuery({
                  page: selected + 1,
                });
              }}
              totalItems={totalItems}
              itemsPerPage={itemsPerPage}
            />
          </div>
        )}
      </div>

    </>
  );
};

export default List;
